import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

interface UseSectionActiveOptions {
  /** Called whenever the active slot index changes. */
  onSectionChange?: (index: number) => void;
}

/**
 * Marks each direct child of `trackRef` with `data-active="true"` the
 * first time it enters the viewport. Entrance animations in
 * `useFadeUpOnScroll` and `AnimatedNumber` react to that attribute.
 *
 * Sections flow naturally (no sticky stacking), so the trigger is a
 * straightforward IntersectionObserver per section. The attribute is
 * set once and never cleared — animations play once per section.
 */
export const useSectionActive = (
  trackRef: RefObject<HTMLElement>,
  options: UseSectionActiveOptions = {},
): void => {
  const onChangeRef = useRef(options.onSectionChange);
  onChangeRef.current = options.onSectionChange;

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const sections = Array.from(track.querySelectorAll<HTMLElement>(':scope > *'));
    if (sections.length === 0) return;

    // Header height differs by breakpoint: mobile 56px, desktop 64px.
    const getHeaderHeight = (): number =>
      window.matchMedia('(max-width: 720px)').matches ? 56 : 64;

    const HEADER = getHeaderHeight();

    let lastActiveIndex = -1;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = sections.indexOf(entry.target as HTMLElement);
            entry.target.setAttribute('data-active', 'true');
            entry.target.setAttribute('aria-current', 'true');
            if (idx !== -1 && idx !== lastActiveIndex) {
              lastActiveIndex = idx;
              if (onChangeRef.current) onChangeRef.current(idx);
            }
          }
        });
      },
      {
        // Account for the fixed header so the trigger fires when the
        // section's content is actually visible, not just its top edge.
        rootMargin: `-${HEADER}px 0px -10% 0px`,
        threshold: 0.05,
      },
    );

    sections.forEach((s) => observer.observe(s));

    return () => {
      observer.disconnect();
      sections.forEach((s) => {
        s.removeAttribute('data-active');
        s.removeAttribute('aria-current');
      });
    };
  }, [trackRef]);
};

/** Backwards-compatible alias. */
export const useHorizontalPager = useSectionActive;
export const useSectionSnap = useSectionActive;
